import {inject, Injectable, signal} from "@angular/core";
import {filter, map, Observable, shareReplay} from "rxjs";
import {
	APPLICATIONS_URL,
	AppRouteUrl,
	DATA_URL,
	DIAGRAMS_CAPACITY_URL,
	INFRASTRUCTURE_URL,
	WORKSTATIONS_DASHBOARD_URL
} from "../../models/home/navigation.model";
import {NavigationEnd, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {Permission} from "../auth/permissions.service";
import {AccountService} from "../accounts/account.service";

@Injectable({providedIn: 'root'})
export class NewsService {

	private router: Router = inject(Router);
	private translate: TranslateService = inject(TranslateService);
	private accountService: AccountService = inject(AccountService);

	private readonly signupDate$ = this.accountService.getSignupDate().pipe(shareReplay(1));

	private readonly allNews: NewsElement[] = [
		{
			id: 'infrastructure-news-1',
			icon: 'assets/icons/news/infrastructure-news-1.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.infrastructureNews1.title',
			content: 'page.home.news.infrastructureNews1.content',
			image: 'assets/illustrations/news/infrastructure-news-1.png',
			creationDate: new Date(2025, 0, 13),
			expirationDate: new Date(2025, 2, 13),
			buttonLabel: 'page.home.news.infrastructureNews1.button',
			buttonRoute: INFRASTRUCTURE_URL
		},
		{
			id: 'data-news-1',
			icon: 'assets/icons/news/data-news-1.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.dataNews1.title',
			content: 'page.home.news.dataNews1.content',
			image: 'assets/illustrations/news/data-news-1.svg',
			creationDate: new Date(2025, 1, 28),
			expirationDate: new Date(2025, 3, 28),
			buttonLabel: 'page.home.news.dataNews1.button',
			buttonRoute: DATA_URL
		},
		{
			id: 'agent-news-1',
			icon: 'assets/icons/news/agent-news-1.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.agentNews1.title',
			content: 'page.home.news.agentNews1.content',
			image: 'assets/illustrations/news/agent-news-1.svg',
			creationDate: new Date(2025, 3, 8),
			expirationDate: new Date(2025, 4, 9)
		},
		{
			id: 'workstation-news-1',
			icon: 'assets/icons/news/workstation-news-1.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.workstationNews1.title',
			content: 'page.home.news.workstationNews1.content',
			image: 'assets/illustrations/news/workstation-news-1.svg',
			creationDate: new Date(2025, 3, 8),
			expirationDate: new Date(2025, 5, 9)
		},
		{
			id: 'capacity-news-1',
			icon: 'assets/icons/news/capacity-news-1.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.capacityNews1.title',
			content: 'page.home.news.capacityNews1.content',
			image: 'assets/illustrations/news/capacity-news-1.svg',
			creationDate: new Date(2025, 6, 16),
			expirationDate: new Date(2025, 10, 16),
			buttonLabel: 'page.home.news.capacityNews1.button',
			buttonRoute: DIAGRAMS_CAPACITY_URL
		},
		{
			id: 'custom-fields-news-1',
			icon: 'assets/icons/news/custom-fields-news-1.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.customFieldsNews1.title',
			content: 'page.home.news.customFieldsNews1.content',
			image: 'assets/illustrations/news/custom-fields-news-1.svg',
			creationDate: new Date(2025, 9, 5),
			expirationDate: new Date(2026, 0, 5),
		},
		{
			id: 'workstation-dashboard-news-1',
			icon: 'assets/icons/news/bar-chart-10.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.workstationDashboardNews1.title',
			content: 'page.home.news.workstationDashboardNews1.content',
			image: `assets/illustrations/news/workstation-dashboard-news-1-${this.translate.getCurrentLang() === 'fr' ? 'fr' : 'en'}.svg`,
			creationDate: new Date(2026, 6, 1),
			expirationDate: new Date(2026, 8, 1),
			buttonLabel: 'page.home.news.workstationDashboardNews1.button',
			buttonRoute: WORKSTATIONS_DASHBOARD_URL,
			permission: Permission.WORKSTATIONS_DASHBOARD_READ
		},
		{
			id: 'shadow-it-news-1',
			icon: 'assets/icons/news/grid-01.svg',
			iconBackgroundColor: 'rgba(168, 144, 243, 0.10)',
			title: 'page.home.news.shadowItNews1.title',
			content: 'page.home.news.shadowItNews1.content',
			image: 'assets/illustrations/news/shadow-it-news-1.svg',
			creationDate: new Date(2026, 8, 1),
			expirationDate: new Date(2026, 10, 1),
			buttonLabel: 'page.home.news.shadowItNews1.button',
			buttonRoute: APPLICATIONS_URL
		},
	];

	private readonly allNewsBadge: NewsBadge[] = [
		{
			id: "workstation-dashboard-news-1",
			route: WORKSTATIONS_DASHBOARD_URL,
			creationDate: new Date(2026, 6, 1),
			expirationDate: new Date(2026, 8, 1),
			permission: Permission.WORKSTATIONS_DASHBOARD_READ
		}
	]

	readonly news: NewsElement[] = this.allNews
		.filter(news => news.expirationDate > new Date())
		.sort((a, b) => a.creationDate.getTime() - b.creationDate.getTime());

	readonly newsBadge: NewsBadge[] = this.allNewsBadge
		.filter(news => news.expirationDate > new Date())
		.sort((a, b) => a.creationDate.getTime() - b.creationDate.getTime());

	readonly relevantNewsBadge$: Observable<NewsBadge[]> = this.signupDate$.pipe(
		map(signupDate => this.newsBadge.filter(badge => badge.creationDate > new Date(signupDate)))
	);

	private readonly _seenNews = signal<string[]>(this.loadSeenNews());
	private readonly _visitedBadge = signal<string[]>(this.loadVisitedBadge());
	/** The badges whose page the user has visited. */
	readonly visitedBadge = this._visitedBadge.asReadonly();

	constructor() {
		this.router.events
			.pipe(filter(e => e instanceof NavigationEnd))
			.subscribe((e: NavigationEnd) => {
				const path = e.urlAfterRedirects.split('?')[0];
				const matched = this.newsBadge.filter(n => n.route === path).map(n => n.id);
				if (matched.length) this.markAsVisited(matched);
			});
	}

	get unseenNews(): NewsElement[] {
		return this.news.filter(n => !this._seenNews().includes(n.id));
	}

	markAsSeen(newsIds: string[]): void {
		const updated = [...new Set([...this._seenNews(), ...newsIds])];
		this._seenNews.set(updated);
		this.persistSeenNews(updated);
	}

	markAllAsSeen(): void {
		this.markAsSeen(this.news.map(n => n.id));
	}

	private loadSeenNews(): string[] {
		try {
			const stored = localStorage.getItem('seenNews');
			if (!stored) return [];
			const parsed = JSON.parse(stored);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	private persistSeenNews(ids: string[]): void {
		try {
			localStorage.setItem('seenNews', JSON.stringify(ids));
		} catch {
			// localStorage quota exceeded or unavailable
		}
	}

	markAsVisited(newsIds: string[]): void {
		const updated = [...new Set([...this._visitedBadge(), ...newsIds])];
		this._visitedBadge.set(updated);
		this.persistVisitedBadge(updated);
	}

	private loadVisitedBadge(): string[] {
		try {
			const stored = localStorage.getItem('visitedBadge');
			if (!stored) return [];
			const parsed = JSON.parse(stored);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	private persistVisitedBadge(ids: string[]): void {
		try {
			localStorage.setItem('visitedBadge', JSON.stringify(ids));
		} catch {
			// localStorage quota exceeded or unavailable
		}
	}
}

export interface NewsElement {
	id: string;
	icon: string;
	iconBackgroundColor: string;
	title: string;
	content: string;
	image: string;
	creationDate: Date;
	expirationDate: Date;
	buttonLabel?: string;
	buttonRoute?: string;
	permission?: Permission;
}

export interface NewsBadge {
	id: string;
	route: AppRouteUrl;
	creationDate: Date;
	expirationDate: Date;
	permission?: Permission;
}
